<template>
<form
  id="address-form-container"
  novalidate
  class="form"
  @submit.prevent="onApplyButtonClick"
  tabindex="-1"
>
  <div class="form_flex_container">

    <div id="address-form-input">

      <!-- SINGLELINE -->
      <div
        v-show="isSingleLineFastCompletionEnabled"
        class="custom-field"
        :style="{ order: 101 }"
      >
        <OneTolerantSuggestion
          v-model.trim="singleline"
          :form="v$.form"
          :label="$t('profile_personal_data_add_address_singleline_label')"
          :placeholder="''"
          :profileStyle="true"
          :paramName="'singleline'"
          id="singleline"
          :errors="[]"
          :univoqueBy="['postalcode', 'city', 'provinceCode', 'HouseNumber', 'street', 'building', 'subbuilding']"
          :isSuggestionEnabled="isSingleLineFastCompletionEnabled"
          :tolerantProfileKey="TOLERANT_PROFILE_KEY.FC_SINGLE_LINE"
          :other-parameters="form"
          @suggestion-selected="applySuggestionNew($event)"
        />
      </div>

    <!-- HOUSE NUMBER -->
      <div
        v-show="!isSingleLineFastCompletionEnabled"
        class="custom-field"
        :style="{ order: getFieldOrder('houseNumber') }"
      >
        <OneTolerantSuggestion
          v-model.trim="form.houseNumber"
          :form="v$.form"
          :label="$t('profile_personal_data_add_or_edit_address_houseNumber_label') + (isRequired('houseNumber') ? '*' : '')"
          :profileStyle="true"
          :paramName="'houseNumber'"
          :id="'houseNumber'"
          @blur="touchForm('houseNumber')"
          @input="touchForm('houseNumber')"
          :errors="[
            {message:$t('profile_personal_data_add_or_edit_address_houseNumber_error_required'), thereIsTheError:v$.form.houseNumber.required.$invalid},
            {message:$t('profile_error_min_length_prefix')+getMinLength('houseNumber')+$t('profile_error_min_length_suffix'), thereIsTheError:v$.form.houseNumber.minLength.$invalid},
            {message:$t('profile_error_max_length_prefix')+getMaxLength('houseNumber')+$t('profile_error_max_length_suffix'), thereIsTheError:v$.form.houseNumber.maxLength.$invalid},
          ]"
          :isSuggestionEnabled="isTolerantOnStreetNumberEnabled"
          :tolerantProfileKey="TOLERANT_PROFILE_KEY.FC_HOUSE_NUMBER"
          :other-parameters="form"
          :minimumCharactersSuggestion="0"
          @suggestion-selected="applySuggestionNew($event)"
          :disabled="isFieldDisabled('houseNumber')"
        />
      </div>

      <!-- STREET -->
      <div
        v-show="!isSingleLineFastCompletionEnabled"
        class="custom-field"
        :style="{ order: getFieldOrder('street') }"
      >
        <OneTolerantSuggestion
          v-model.trim="form.street"
          :form="v$.form"
          :label="$t('profile_personal_data_add_or_edit_address_street') + (isRequired('street') ? '*' : '')"
          :profileStyle="true"
          :paramName="'street'"
          :id="'street'"
          @blur="touchForm('street')"
          @input="touchForm('street')"
          :errors="[
            {message:$t('profile_personal_data_add_or_edit_address_street_error_required'), thereIsTheError:v$.form.street.required.$invalid},
            {message:$t('profile_error_min_length_prefix')+getMinLength('street')+$t('profile_error_min_length_suffix'), thereIsTheError:v$.form.street.minLength.$invalid},
            {message:$t('profile_error_max_length_prefix')+getMaxLength('street')+$t('profile_error_max_length_suffix'), thereIsTheError:v$.form.street.maxLength.$invalid},
          ]"
          :isSuggestionEnabled="isTolerantOnStreetEnabled"
          :tolerantProfileKey="TOLERANT_PROFILE_KEY.FC_STREET"
          :other-parameters="form"
          @suggestion-selected="applySuggestionNew($event)"
          :disabled="isFieldDisabled('street')"
        />
      </div>

      <!-- ADDITIONAL LINE 1 -->
      <div
        v-show="!isSingleLineFastCompletionEnabled"
        class="custom-field"
        :style="{ order: getFieldOrder('strSuppl1') }"
      >
        <OneInput
          :brandClass="brandClass()"
          :profileStyle="true"
          v-model.trim="form.strSuppl1"
          :form="v$.form"
          :label="$t('profile_personal_data_add_or_edit_address_strSuppl1')+(isRequired('strSuppl1') ? '*' : '')"
          :paramName="'strSuppl1'"
          :id="'strSuppl1'"
          @blur="touchForm('strSuppl1')"
          @input="touchForm('strSuppl1')"
          :errors="[
            {message:$t('profile_error_min_length_prefix')+getMinLength('strSuppl1')+$t('profile_error_min_length_suffix'), thereIsTheError:v$.form.strSuppl1.minLength.$invalid},
            {message:$t('profile_error_max_length_prefix')+getMaxLength('strSuppl1')+$t('profile_error_max_length_suffix'), thereIsTheError:v$.form.strSuppl1.maxLength.$invalid},
          ]"
          :disabled="isFieldDisabled('strSuppl1')"
        />
      </div>

      <!-- ADDITIONAL LINE 2 -->
      <div
        v-show="!isSingleLineFastCompletionEnabled"
        class="custom-field"
        :style="{ order: getFieldOrder('strSuppl2') }"
      >
        <OneInput
          :brandClass="brandClass()"
          :profileStyle="true"
          v-model.trim="form.strSuppl2"
          :form="v$.form"
          :label="$t('profile_personal_data_add_or_edit_address_strSuppl2')+(isRequired('strSuppl2') ? '*' : '')"
          :paramName="'strSuppl2'"
          :id="'strSuppl2'"
          @blur="touchForm('strSuppl2')"
          @input="touchForm('strSuppl2')"
          :errors="[
            {message:$t('profile_error_min_length_prefix')+getMinLength('strSuppl2')+$t('profile_error_min_length_suffix'), thereIsTheError:v$.form.strSuppl2.minLength.$invalid},
            {message:$t('profile_error_max_length_prefix')+getMaxLength('strSuppl2')+$t('profile_error_max_length_suffix'), thereIsTheError:v$.form.strSuppl2.maxLength.$invalid},
          ]"
          :disabled="isFieldDisabled('strSuppl2')"
        />
      </div>

      <!-- POSTAL CODE -->
      <div
        v-show="!isSingleLineFastCompletionEnabled"
        class="custom-field"
        :style="{ order: getFieldOrder('postalCode') }"
      >
        <OneTolerantSuggestion
          v-model.trim="form.postalCode"
          :form="v$.form"
          :label="$t('profile_personal_data_add_or_edit_address_postalCode')+(isRequired('postalCode') || form.street ? '*' : '')"
          :profileStyle="true"
          :paramName="'postalCode'"
          :id="'postalCode'"
          @blur="touchForm('postalCode')"
          @input="touchForm('postalCode')"
          :errors="[
              {message:$t('profile_personal_data_add_or_edit_address_postalCode_error_required'), thereIsTheError:v$.form.postalCode.required.$invalid},
              {message:$t('profile_error_min_length_prefix')+getMinLength('postalCode')+$t('profile_error_min_length_suffix'), thereIsTheError:v$.form.postalCode.minLength.$invalid},
              {message:$t('profile_error_max_length_prefix')+getMaxLength('postalCode')+$t('profile_error_max_length_suffix'), thereIsTheError:v$.form.postalCode.maxLength.$invalid},
              {message:$t('profile_personal_data_add_or_edit_address_postalCode_error_valid'), thereIsTheError:v$.form.postalCode.regEx.$invalid},
            ]"
          :univoqueBy="['postalcode', 'city', 'provinceCode']"
          :isSuggestionEnabled="isTolerantOnPostalCodeEnabled"
          :tolerantProfileKey="TOLERANT_PROFILE_KEY.FC_POSTAL_CODE"
          :other-parameters="form"
          @suggestion-selected="applySuggestionNew($event)"
          :disabled="isFieldDisabled('postalCode')"
        />
      </div>

      <!-- CITY -->
      <div
        v-show="!isSingleLineFastCompletionEnabled"
        class="custom-field"
        :style="{ order: getFieldOrder('city') }"
      >
        <OneTolerantSuggestion
          v-model.trim="form.city"
          :form="v$.form"
          :label="$t('profile_personal_data_add_or_edit_address_city')+(isRequired('city') || form.street ? '*' : '')"
          :profileStyle="true"
          :paramName="'city'"
          :id="'city'"
          @blur="touchForm('city')"
          @input="touchForm('city')"
          :errors="[
                {message:$t('profile_personal_data_add_or_edit_address_city_error_required'), thereIsTheError:v$.form.city.required.$invalid},
                {message:$t('profile_error_min_length_prefix')+getMinLength('city')+$t('profile_error_min_length_suffix'), thereIsTheError:v$.form.city.minLength.$invalid},
                {message:$t('profile_error_max_length_prefix')+getMaxLength('city')+$t('profile_error_max_length_suffix'), thereIsTheError:v$.form.city.maxLength.$invalid},
              ]"
          :isSuggestionEnabled="isTolerantOnCityEnabled"
          :tolerantProfileKey="TOLERANT_PROFILE_KEY.FC_CITY"
          :other-parameters="form"
          @suggestion-selected="applySuggestionNew($event)"
          :disabled="isFieldDisabled('city')"
        />
      </div>

      <!-- REGION CODE INPUT -->
      <div
        v-show="isRegionCodeVisible(form.country) && !isSingleLineFastCompletionEnabled && (!$c('enable_region_codes_from_server') || !regionCodesFromServerPerCurrentLanguage.length)"
        class="custom-field"
        :style="{ order: getFieldOrder('regionCode') }"
      >
        <OneInput
          :brandClass="brandClass()"
          :profileStyle="true"
          v-model.trim="form.regionCode"
          :form="v$.form"
          :label="$t('profile_personal_data_add_or_edit_address_regionCode')+(isRegionCodeRequired ? '*' : '')"
          :paramName="'regionCode'"
          :id="'regionCode'"
          @blur="touchForm('regionCode')"
          @input="touchForm('regionCode')"
          :errors="[
            {message:$t('profile_personal_data_add_or_edit_address_houseNumber_error_required'), thereIsTheError:v$.form.regionCode.required.$invalid},
            {message:$t('profile_error_min_length_prefix')+getMinLength('regionCode')+$t('profile_error_min_length_suffix'), thereIsTheError:v$.form.regionCode.minLength.$invalid},
            {message:$t('profile_error_max_length_prefix')+getMaxLength('regionCode')+$t('profile_error_max_length_suffix'), thereIsTheError:v$.form.regionCode.maxLength.$invalid},
          ]"
          :disabled="isFieldDisabled('regionCode')"
        />
      </div>

      <!-- REGION CODE SELECT -->
      <div
        v-show="isRegionCodeVisible(form.country) && !isSingleLineFastCompletionEnabled && $c('enable_region_codes_from_server') && regionCodesFromServerPerCurrentLanguage.length"
        class="custom-field"
        :style="{ order: getFieldOrder('regionCode') }"
      >
        <OneSelect
          :brandClass="brandClass()"
          :t="$t"
          :profileStyle="true"
          v-model="form.regionCode"
          :form="v$.form"
          :paramName="'regionCode'"
          :options="regionCodesFromServerPerCurrentLanguage"
          @blur="touchForm('regionCode')"
          @input="touchForm('regionCode')"
          :isSearchEnabled="true"
          :label="$t('profile_personal_data_add_or_edit_address_regionCode')+(isRegionCodeRequired ? '*' : '')"
          :errors="[
            {message:$t('profile_regionCode_error_required'), thereIsTheError:v$.form.regionCode.required.$invalid}
          ]"
          :disabled="isFieldDisabled('regionCode')"
        ></OneSelect>
      </div>

      <!-- COUNTRY -->
      <div
        class="custom-field"
        :style="{ order: getFieldOrder('country') }"
      >
        <OneSelect
          :brandClass="brandClass()"
          :t="$t"
          :profileStyle="true"
          v-model="form.country"
          :form="v$.form"
          :paramName="'country'"
          :disabled="thereAreNoCountries || isFieldDisabled('country')"
          :options="selectedOptions"
          @blur="touchForm('country')"
          @input="touchForm('country')"
          :isSearchEnabled="true"
          :label="$t('profile_personal_data_add_or_edit_address_country')+(isRequired('country') ? '*' : '')"
          :errors="[
            {message:$t('profile_personal_data_add_or_edit_address_error_required'), thereIsTheError:v$.form.country.required.$invalid}
          ]"
        ></OneSelect>
      </div>

      <!-- COMPANY NAME -->
      <div
        v-if="v$.form.type.$model === 'BUSINESS'"
        v-show="!isSingleLineFastCompletionEnabled"
        class="custom-field"
        :style="{ order: getFieldOrder('companyName') }"
      >
        <OneInput
          :brandClass="brandClass()"
          :profileStyle="true"
          v-model.trim="form.companyName"
          :form="v$.form"
          :label="$t('profile_personal_data_add_or_edit_address_companyName')+(isRequired('companyName') ? '*' : '')"
          :paramName="'companyName'"
          :id="'companyName'"
          @blur="touchForm('companyName')"
          @input="touchForm('companyName')"
          :errors="[
            {message:$t('profile_error_min_length_prefix')+getMinLength('companyName')+$t('profile_error_min_length_suffix'), thereIsTheError:v$.form.companyName.minLength.$invalid},
            {message:$t('profile_error_max_length_prefix')+getMaxLength('companyName')+$t('profile_error_max_length_suffix'), thereIsTheError:v$.form.companyName.maxLength.$invalid},
          ]"
          :disabled="isFieldDisabled('companyName')"
        />
      </div>

      <!-- PO BOX -->
      <div
        v-if="v$.form.type.$model === 'BUSINESS'"
        v-show="!isSingleLineFastCompletionEnabled"
        class="custom-field"
        :style="{ order: getFieldOrder('postOfficeBox') }"
      >
        <OneInput
          :brandClass="brandClass()"
          :profileStyle="true"
          v-model.trim="form.postOfficeBox"
          :form="v$.form"
          :label="$t('profile_personal_data_add_or_edit_address_postOfficeBox')+(isRequired('postOfficeBox') ? '*' : '')"
          :paramName="'postOfficeBox'"
          :id="'postOfficeBox'"
          @blur="touchForm('postOfficeBox')"
          @input="touchForm('postOfficeBox')"
          :errors="[
            {message:$t('profile_error_min_length_prefix')+getMinLength('postOfficeBox')+$t('profile_error_min_length_suffix'), thereIsTheError:v$.form.postOfficeBox.minLength.$invalid},
            {message:$t('profile_error_max_length_prefix')+getMaxLength('postOfficeBox')+$t('profile_error_max_length_suffix'), thereIsTheError:v$.form.postOfficeBox.maxLength.$invalid},
          ]"
          :disabled="isFieldDisabled('postOfficeBox')"
        />
      </div>


<!--      <div>ProfileAddressesForm form: {{form}}</div>-->
    </div>

    <!-- Mandatory fields info text -->
    <div class="mandatory" style="order:100; margin-top: 10px">
      *{{$t("generic_mandatory_fields")}}
    </div>

    <!-- Apply / cancel buttons container -->
    <div class="buttonsContainer" style="order:100; width:100%" >
      <!-- Apply button -->
      <OneButton
        :brandClass="brandClass()"
        :profileStyle="true"
        :type="'primary'"
        :label="
          editMode === ACTION.ADD ?
            $t('profile_personal_data_add_or_edit_address_insert_button') :
            $t('profile_apply_button')"
        :disabled="isButtonDisabled"
        @click="onApplyButtonClick('address-form-container')"
      />

      <!-- Cancel button -->
      <OneButton
        :brandClass="brandClass()"
        :profileStyle="true"
        :type="'secondary'"
        :label="$t('profile_cancel_button')"
        @click="onCancelButtonClick()"
      />
    </div>
  </div>
</form>
</template>

<style lang="scss" scoped>
.form {
  margin-top: 0 !important;
  padding: 0 !important;
  padding-top: 22px !important;
}
#address-form-input {
  width: 100%;
  display: flex; 
  flex-direction: column;
  .custom-field {
    margin: 0 0 9px;
  }
}
</style>

<script>
import oneUtils from "../../../../gcdm-one-library/gcdm-one-utils/src/bootstrap";
import useVuelidate from '@vuelidate/core';
import { defineComponent } from 'vue';
import {
  required,
  requiredIf
} from "@vuelidate/validators";
import { defineAsyncComponent } from 'vue'
import VueJsonPretty from "vue-json-pretty";
import OneTolerantSuggestion from "@/components/ProfileForm/components/OneTolerantSuggestion.vue";

export default defineComponent({
  name: "ProfileAddressesForm",
  inject:[
    '$c',
    '$t',
    'brandClass'
  ],
  mixins: [
    oneUtils.customValidationMixin,
    oneUtils.accessibilityMixin
  ],
  components:{
    OneTolerantSuggestion,
    VueJsonPretty,
    OneCheckbox: defineAsyncComponent(() =>
      import('../../../../gcdm-one-library/gcdm-one-checkbox/src/OneCheckbox')
    )
  },
  props: {
    form: {},
    editMode: null
  },
  emits: [
    "onCancelButtonClick",
    "onApplyButtonClick"
  ],
  setup() {
    return {
      v$: useVuelidate()
    }
  },
  watch: {
    "form.country": {
      handler(value) {
        if (this.$c("enable_region_codes_from_server")) {
          this.getRegionCodesFromServer(value);
        }
        // reset region code
        this.form.regionCode = null;
        if(this.form.postalCode) {
          this.touchForm("postalCode");
        }
      }
    },
    "$store.state.configurationParams": {
      deep: true,
      handler(newVal, oldVal) {
        if (newVal.language !== oldVal.language) {
          if(this.$c('enable_region_codes_from_server')) {
            this.getRegionCodesFromServer();
          }
        }
      },
    },
  },
  data: () => ({
    singleline:'',
    ACTION: oneUtils.enums.actionMode,
    fastCompletionResults:{},
    isSuggestionApplied: false,
    TOLERANT_PROFILE_KEY: oneUtils.enums.tolerantProfileKey,
    regionCodesFromServer: null,
    regionCodesFromServerPerCurrentLanguage: []
  }),
  validations: {
    form: {
      singleline: { },
      type: {
        required,
      },
      street: {
        minLength: function(value) {
          return this.isMinLengthValid('street', value);
        },
        maxLength: function(value) {
          return this.isMaxLengthValid('street', value);
        },
        required: requiredIf(function (model) {
          return this.isRequired('street');
        }),
      },
      houseNumber: {
        minLength: function(value) {
          return this.isMinLengthValid('houseNumber', value);
        },
        maxLength: function(value) {
          return this.isMaxLengthValid('houseNumber', value);
        },
        required: requiredIf(function (model) {
          return this.isRequired('houseNumber');
        }),
      },
      postalCode: {
        minLength: function(value) {
          if(!this.sectionConfig?.['postalCode']?.countryValidation?.[this.form.country]) {
            return this.isMinLengthValid('postalCode', value);
          }
          else {
            return true;
          }
        },
        maxLength: function(value) {
          if(!this.sectionConfig?.['postalCode']?.countryValidation?.[this.form.country]) {
            return this.isMaxLengthValid('postalCode', value);
          }
          else {
            return true;
          }
        },
        regEx: function (value) {
          return this.isPostalCodeRegExValid("postalCode", value, this.form.country);
        },
        required: requiredIf(function (model) {
          return this.isRequired('postalCode') || this.form.street;
        }),
      },
      city: {
        minLength: function(value) {
          return this.isMinLengthValid('city', value);
        },
        maxLength: function(value) {
          return this.isMaxLengthValid('city', value);
        },
        required: requiredIf(function (model) {
          return !this.isFieldDisabled('city') && (this.isRequired('city') || this.form.street);
        }),
      },
      country: {
        required: requiredIf(function (model) {
          return this.isRequired('country');
        }),
      },
      preferred: {},
      strSuppl1: {
        minLength: function(value) {
          return this.isMinLengthValid('strSuppl1', value);
        },
        maxLength: function(value) {
          return this.isMaxLengthValid('strSuppl1', value);
        },
        required: requiredIf(function (model) {
          return this.isRequired('strSuppl1');
        }),
      },
      strSuppl2: {
        minLength: function(value) {
          return this.isMinLengthValid('strSuppl2', value);
        },
        maxLength: function(value) {
          return this.isMaxLengthValid('strSuppl2', value);
        },
        required: requiredIf(function (model) {
          return this.isRequired('strSuppl2');
        }),
      },
      companyName: {
        minLength: function (value) {
          return this.isMinLengthValid('companyName', value);
        },
        maxLength: function (value) {
          return this.isMaxLengthValid('companyName', value);
        }
      },
      postOfficeBox: {
        minLength: function (value) {
          return this.isMinLengthValid('postOfficeBox', value);
        },
        maxLength: function (value) {
          return this.isMaxLengthValid('postOfficeBox', value);
        }
      },
      regionCode: {
        minLength: function (value) {
          return this.isMinLengthValid('regionCode', value);
        },
        maxLength: function (value) {
          return this.isMaxLengthValid('regionCode', value);
        },
        // isRegionCodeValid: function (value) {
        //   if(this.$c('enable_region_codes_from_server')) {
        //     return this.regionCodesFromServerPerCurrentLanguage.some(rc => rc.value === value);
        //   }
        //   return false;
        // },
        required: requiredIf(function (model) {
          if(this.isRegionCodeVisible(this.form.country)) {
            return this.isRegionCodeRequired;
          }
        }),
      },
    },
  },
  mounted() {
    // check if region codes should be retrieved from server or from one-configuration
    if (this.$c('enable_region_codes_from_server')){
      this.getRegionCodesFromServer();
    }
  },
  computed: {
    sectionConfig() {
      return this.$c("_profileConfiguration").addressesSection;
    },
    selectedOptions: function(){
      return this.getSelectOptions('list_of_countries');
    },
    thereAreNoCountries: function(){
      return this.selectedOptions.length === 0;
    },
    isButtonDisabled: function () {
      return (
        this.v$.$invalid
      );
    },
    isSingleLineFastCompletionEnabled() {
      return this.$c("enable_tolerant_singleline") && this.editMode === oneUtils.enums.actionMode.ADD && !this.isSuggestionApplied;
    },
    isTolerantOnPostalCodeEnabled() {
      return this.$c('enable_tolerant_postalcode');
    },
    isTolerantOnCityEnabled() {
      return this.$c("enable_tolerant_city");
    },
    isTolerantOnStreetEnabled() {
      return this.$c('enable_tolerant_street');
    },
    isTolerantOnStreetNumberEnabled() {
      return this.$c("enable_tolerant_housenumber");
    },
    isRegionCodeRequired() {
      if (this.$c('enable_region_codes_from_server')) return this.regionCodesFromServerPerCurrentLanguage.length && this.isRequired('regionCode');
      else return this.isRequired('regionCode')
    }
  },
  methods: {
    isRegionCodeVisible(country) {
      return !this.sectionConfig.addressesSectionByCountrySelection?.[country]?.hide_region_code;
    },
    getFieldOrder(fieldName) {
      if (this.form.country &&
        this.sectionConfig.addressesSectionByCountrySelection &&
        this.sectionConfig.addressesSectionByCountrySelection[this.form.country] && 
        this.sectionConfig.addressesSectionByCountrySelection[this.form.country][fieldName]) {
          return this.getCountrySpecificFormOrder(fieldName, "addressesSectionByCountrySelection", this.form.country);
      }
      return this.getFormOrder(fieldName);
    },
    isFieldDisabled(key) {
      // check if the field depends on another by "disabledBy" property
      let disabledByKeyByCountrySelected = null;
      if(this.sectionConfig.addressesSectionByCountrySelection && this.sectionConfig.addressesSectionByCountrySelection[this.form.country] && this.sectionConfig.addressesSectionByCountrySelection[this.form.country][key]){
        disabledByKeyByCountrySelected = this.sectionConfig.addressesSectionByCountrySelection[this.form.country][key].disabledBy;
      }
      if ((!this.sectionConfig || !this.sectionConfig[key] || !this.sectionConfig[key].disabledBy) && (!disabledByKeyByCountrySelected || disabledByKeyByCountrySelected=='')){
        return false;
      } 
      if(disabledByKeyByCountrySelected==''){
        return false;
      } 
      const disabledByKey = disabledByKeyByCountrySelected ? disabledByKeyByCountrySelected : this.sectionConfig[key].disabledBy;
      if ((this.form && this.form[disabledByKey]) || this.form[key]){
        return false;
      } 
      else{
        return true;
      }
    },
    applySuggestionNew(address){
      // console.log(`applySuggestionNew - `, address);
      for (const key in address) {
        if(key === "regionCode" && !this.isRegionCodeVisible(this.form.country)) {
          continue;
        }

        //[GCDM-237883] Show region as regions in addressbook: Implementation 
        if(key === "regionCode") {
          this.form[key] = this.remapRegionCode(address[key], this.form.country);
        } else {
          this.form[key] = address[key];
        }

        this.touchForm(key);
      }
      this.isSuggestionApplied = true;
    },

    //[GCDM-237883] Show region as regions in addressbook: Implementation 
    remapJapanRegionCode(value) {
      return this.regionCodesFromServerPerCurrentLanguage.find((rc) => rc.label === value.replace("JP ", ""))?.value ?? value;
    },

    //[GCDM-268189] OneProfile-Addresses-Defects
    remapAustraliaRegionCode(value) {
      if(!this.regionCodesFromServerPerCurrentLanguage.length) return value.replace("AU ", "");
      return this.regionCodesFromServerPerCurrentLanguage.find((rc) => rc.value === value.replace("AU ", ""))?.value ?? value;
    },

    //[GCDM-268189] OneProfile-Addresses-Defects
    remapRegionCode(value, country) {
      if(!value) return null;

      if(country === "JP") return this.remapJapanRegionCode(value);
      if(country === "AU") return this.remapAustraliaRegionCode(value);

      return value;
    },

    //this method recovers the regions from the server
    getRegionCodesFromServer(){
      oneUtils.utils.getRegionCodesFromServer(this.form.country).then((regionCodes)=>{
        //all regions
        this.regionCodesFromServer = regionCodes;
        //regions for selected language
        this.regionCodesFromServerPerCurrentLanguage = oneUtils.utils.getRegionCodesByCurrentLanguage(regionCodes);
        //[GCDM-237883] Show region as regions in addressbook: Implementation 
        this.form.regionCode = this.remapRegionCode(this.form.regionCode, this.form.country);
        // reset region if not found
        const isRegionValid = this.regionCodesFromServerPerCurrentLanguage.some(rc => rc.value === this.form.regionCode);
        if (!isRegionValid) this.form.regionCode = null;
      }).catch();
    },
    /**
     * Cancel button click event emits the event to the parent
     */
    onCancelButtonClick(){
      this.$emit("onCancelButtonClick");
    },
    /**
     * Apply button click event emits the event to the parent
     */
    onApplyButtonClick(elementId = "") {
      if(this.isSingleLineFastCompletionEnabled){
        this.applySuggestionNew({})
        if(elementId) {
          setTimeout(() => {
            document.getElementById(elementId)?.focus();
            document.getElementById(elementId)?.blur();
          }, 0)
        }
      }else{
        //[GCDM-237883] Show region as regions in addressbook: Implementation - show regionCode name instead of region code
        this.$emit("onApplyButtonClick", this.regionCodesFromServerPerCurrentLanguage, this.form.country);
      }
    },
  },
});
</script>
